/* eslint-disable react/prop-types */
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Spinner,
  Text,
  useBreakpointValue,
  useDisclosure,
} from '@chakra-ui/react';
import React, { useCallback, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  CustomTooltipForStationRevenue,
  StationRevenueDetail,
} from 'components/ChartTooltips';
import { formatCurrency, formatCurrencyCompact } from 'utils/CommonFunction';
import { MerchantStationRevenueColor } from 'utils/StaticData';

// RAT-1804 froze the dashboard by rendering one stacked <Bar> series per
// distinct station across every merchant on the X axis (316 x 270 ~ 86k SVG
// nodes). RAT-1926 removes the caps instead of raising them: a station is
// coloured by its RANK inside its own merchant, not by its identity, so the
// series count is the busiest single merchant's station count rather than the
// total number of stations. Bounded render, unbounded data.
const SLOT_PREFIX = 'slot_';

const COLORS = MerchantStationRevenueColor;
const colorAt = (index) => COLORS[index % COLORS.length];

const emptyModel = {
  mode: 'empty',
  rows: [],
  seriesKeys: [],
  topKeyByRow: new Map(),
};

// Build the chart model from the API payload. Everything the render needs is
// precomputed here so no O(n^2) work happens inside recharts' per-cell shape
// callback.
const buildChartModel = (report) => {
  const merchants = report?.merchants;
  if (!merchants?.length) return emptyModel;

  // SINGLE merchant -> the X axis becomes that merchant's stations. This is
  // what a Merchant-role login and a one-merchant filter always see.
  if (merchants.length === 1 && merchants[0].stations?.length) {
    const merchant = merchants[0];
    const rows = merchant.stations.map((station) => ({
      key: String(station.stationId),
      name: String(station.stationId),
      value: station.revenue,
      revenue: station.revenue,
      count: station.count,
    }));

    return {
      mode: 'single',
      rows,
      seriesKeys: ['value'],
      topKeyByRow: new Map(),
    };
  }

  // "Merchant total": one flat bar per merchant, no stacking at all.
  const hasStations = merchants.some((merchant) => merchant.stations?.length);
  if (!hasStations) {
    return {
      mode: 'total',
      rows: merchants.map((merchant) => ({
        key: String(merchant.merchantId),
        name: merchant.merchant,
        value: merchant.revenue,
        revenue: merchant.revenue,
        count: merchant.count,
      })),
      seriesKeys: ['value'],
      topKeyByRow: new Map(),
    };
  }

  // "All stations": stacked by slot. The backend already sorted each
  // merchant's stations by revenue desc, so slot N means "Nth biggest station
  // at this venue" on every bar and the legend stays short.
  let slotCount = 0;
  const rows = merchants.map((merchant) => {
    const stations = merchant.stations ?? [];
    slotCount = Math.max(slotCount, stations.length);

    const row = {
      // Two merchants can share a display name, so the stack lookup is keyed by
      // id. Only the axis label uses the name.
      key: String(merchant.merchantId),
      name: merchant.merchant,
      revenue: merchant.revenue,
      count: merchant.count,
      stations,
    };

    stations.forEach((station, index) => {
      row[`${SLOT_PREFIX}${index}`] = station.revenue;
    });

    return row;
  });

  const seriesKeys = Array.from(
    { length: slotCount },
    (_, index) => `${SLOT_PREFIX}${index}`,
  );

  // Precompute the topmost positive slot per row so the rounded-corner shape
  // callback is O(1) instead of scanning every station.
  const topKeyByRow = new Map();
  rows.forEach((row) => {
    let topKey = null;
    seriesKeys.forEach((key) => {
      if ((row[key] || 0) > 0) topKey = key;
    });
    topKeyByRow.set(row.key, topKey);
  });

  return { mode: 'stacked', rows, seriesKeys, topKeyByRow };
};

// Rounded top for the topmost stacked segment, plain rect otherwise.
const roundedTopPath = (x, y, width, height, fill) => {
  const radius = 5;
  const right = x + width;
  const bottom = y + height;
  return (
    <path
      d={`M ${x},${bottom} L ${x},${y + radius} Q ${x},${y} ${x + radius},${y} L ${right - radius},${y} Q ${right},${y} ${right},${y + radius} L ${right},${bottom} Z`}
      fill={fill}
    />
  );
};

const MerchantRevenueStationGraph = ({ report, isLoading, currency }) => {
  const { mode, rows, seriesKeys, topKeyByRow } = useMemo(
    () => buildChartModel(report),
    [report],
  );

  // Touch has no hover, and the chart is also a horizontal scroll container, so
  // a floating tooltip is unusable there. Below md a tap opens a drawer with
  // the same content instead.
  const isTouchLayout = useBreakpointValue({ base: true, md: false });
  const barWidth = useBreakpointValue({ base: 40, md: 50 }) ?? 50;
  const chartHeight = useBreakpointValue({ base: 260, md: 300 }) ?? 300;
  const labelLength = useBreakpointValue({ base: 8, md: 15 }) ?? 15;
  const axisHeight = useBreakpointValue({ base: 90, md: 120 }) ?? 120;

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedRow, setSelectedRow] = useState(null);

  const handleChartClick = useCallback(
    (state) => {
      const row = state?.activePayload?.[0]?.payload;
      if (!row) return;
      setSelectedRow(row);
      onOpen();
    },
    [onOpen],
  );

  if (isLoading) {
    return (
      <Flex justify="center" align="center" height="300px" width="100%">
        <Spinner size="lg" color="black.500" />
      </Flex>
    );
  }

  if (mode === 'empty') {
    return (
      <Flex justify="center" alignItems="center" height="300px">
        <Text fontSize="18px" fontWeight="bold" color="gray.500">
          No records available
        </Text>
      </Flex>
    );
  }

  const isStacked = mode === 'stacked';
  const barBottomMargin = rows.length < 4 ? -60 : 0;

  return (
    <>
      {/* Only the chart itself may scroll sideways. The recharts tooltip is
          absolutely positioned inside this box, so leaving the vertical axis
          scrollable let a tall tooltip put a scrollbar on the chart frame. */}
      <Box width="100%" overflowX="auto" overflowY="hidden">
        <Box
          minWidth={
            rows.length * barWidth > 600
              ? `${rows.length * barWidth}px`
              : '100%'
          }
          height={chartHeight}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={rows}
              margin={{ top: 20, right: 30, left: 20, bottom: barBottomMargin }}
              barCategoryGap={rows.length < 5 ? '10%' : '20%'}
              onClick={handleChartClick}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                horizontal={true}
                vertical={false}
              />
              <XAxis
                dataKey="name"
                interval={0}
                tick={(props) => {
                  const { x, y, payload } = props;
                  const name = String(payload.value);
                  const truncatedName =
                    rows.length > 4 && name.length > labelLength
                      ? `${name.substring(0, labelLength)}...`
                      : name;

                  return (
                    <text
                      x={x}
                      y={y + 10}
                      textAnchor={rows.length < 4 ? 'middle' : 'end'}
                      fontSize={isTouchLayout ? 10 : 12}
                      transform={
                        rows.length < 4 ? undefined : `rotate(-60, ${x}, ${y})`
                      }
                    >
                      {truncatedName}
                    </text>
                  );
                }}
                height={axisHeight}
              />
              <YAxis
                tickFormatter={(value) =>
                  isTouchLayout
                    ? formatCurrencyCompact(value, currency)
                    : formatCurrency(value, currency)
                }
                axisLine={false}
                tickLine={false}
              />
              {!isTouchLayout && (
                <Tooltip
                  content={
                    <CustomTooltipForStationRevenue
                      colorAt={colorAt}
                      currency={currency}
                    />
                  }
                />
              )}

              {isStacked ? (
                seriesKeys.map((key, index) => {
                  const fill = colorAt(index);
                  return (
                    <Bar
                      key={key}
                      dataKey={key}
                      stackId="a"
                      fill={fill}
                      name={`Station ${index + 1}`}
                      shape={(props) => {
                        const { x, y, width, height, payload } = props;
                        if (!height || height === 0) return null;
                        if (topKeyByRow.get(payload.key) === key) {
                          return roundedTopPath(x, y, width, height, fill);
                        }
                        return (
                          <rect
                            x={x}
                            y={y}
                            width={width}
                            height={height}
                            fill={fill}
                          />
                        );
                      }}
                    />
                  );
                })
              ) : (
                <Bar dataKey="value" radius={[5, 5, 0, 0]}>
                  {rows.map((entry, index) => (
                    <Cell key={entry.key} fill={colorAt(index)} />
                  ))}
                </Bar>
              )}
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>

      <Drawer isOpen={isOpen} placement="bottom" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent borderTopRadius="16px" maxHeight="70vh">
          <DrawerCloseButton />
          <DrawerBody py="5">
            {selectedRow && (
              <StationRevenueDetail
                title={selectedRow.name}
                revenue={selectedRow.revenue}
                count={selectedRow.count}
                stations={selectedRow.stations}
                colorAt={colorAt}
                currency={currency}
                maxRows={50}
              />
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default React.memo(MerchantRevenueStationGraph);
